import type moment from 'moment';

import type { SelectedWorkspace } from '../../../services/dashboardsService/hostContext';
import type { ITocOption } from '../../../services/intakeDataService/types';

export interface WorkSpacesSelect {
  displayName: string;
  value: string;
}

export interface IAdminDashboardStore {
  allowedTabs: TabId[];
  dashboardTabs: DashboardTab[];
  setDashboardTabs: (tabs: DashboardTab[]) => void;
  workspaces: WorkSpacesSelect[];
  selectedWorkspaces: SelectedWorkspace[];
  typeOfCare: ITocOption[];
  careLocations: string[];
  latestDataPoint: number | null;
  displayRtmUnitValue: boolean;
  outcomesTimeline: OutcomesTimeline;
  setOutcomesTimeline: (timeline: OutcomesTimeline) => void;
  setLatestDataPoint(number: number | null): void;
  setFilterWorkspaces: (workspaces: string[]) => void;
  setCohortSlug: (slug: string) => void;
  activeDashboardDeclaresCohort: boolean;
  setActiveDashboardDeclaresCohort: (declared: boolean) => void;
  setFilterCareType: (careType: string[]) => void;
  setFilterCareLocation: (careLocation: string[]) => void;
  filters: GlobalFilters & TabFilters;
  setMetaData: (tabs: StaticTabId[], workspaces: { displayName: string; value: string }[]) => void;
  resetStore: () => void;
  legend: Record<string, boolean | undefined>;
  setLegendItemVisibility: (key: string, isVisible: boolean) => void;
  deleteLegendItem: (key: string) => void;
  setDisplayRtmUnitValue: (value: boolean) => void;
  readonly thisMonth: string;
}

export const TabIds = {
  Utilization: 'utilization',
  FallRisk: 'fall_risk',
  RTM: 'rtm',
  Outcomes: 'outcomes',
} as const;

export const OUTCOMES_TIMELINES = ['eval_to_discharge', 'last_3_months', 'last_month'] as const;

export type OutcomesTimeline = (typeof OUTCOMES_TIMELINES)[number];

export const DEFAULT_OUTCOMES_TIMELINE: OutcomesTimeline = 'last_3_months';

export type StaticTabId = (typeof TabIds)[keyof typeof TabIds];
const TAB_VALUES: readonly StaticTabId[] = Object.values(TabIds);

const DASHBOARD_TAB_PREFIX = 'dashboard__';

export type DashboardTabId = `dashboard__${string}`;

export type TabId = StaticTabId | DashboardTabId;

export interface DashboardTab {
  id: string;
  name: string;
}

export function isStaticTabId(value: unknown): value is StaticTabId {
  return typeof value === 'string' && TAB_VALUES.includes(value as StaticTabId);
}

export function isDashboardTabId(value: unknown): value is DashboardTabId {
  return (
    typeof value === 'string' &&
    value.startsWith(DASHBOARD_TAB_PREFIX) &&
    value.length > DASHBOARD_TAB_PREFIX.length
  );
}

export function isTabId(value: unknown): value is TabId {
  return isStaticTabId(value) || isDashboardTabId(value);
}

export function dashboardTabId(dashboardId: string): DashboardTabId {
  return `${DASHBOARD_TAB_PREFIX}${dashboardId}`;
}

export function dashboardIdOf(tabId: DashboardTabId): string {
  return tabId.slice(DASHBOARD_TAB_PREFIX.length);
}

export interface GlobalFilters {
  workspacesId: string[];
  cohortSlug: string;
  utilization: {
    value: string;
    fromDate: moment.Moment;
    toDate: moment.Moment;
  };
}

export interface TabFilters {
  careType: string[];
  careLocation: string[];
  rtmDate: string | undefined;
}
